import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { defineMessage, FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { createLookupHash } from '../../services/hash-service';
import { entitiesLoad } from '../../store/entities/entities.actions';
import { addPresetGroupToProducts, bulkLoadProducts } from '../../store/products/products.api';
import { ProductPresetGroupPropType } from '../../prop-types/product-preset-group';
import Global from '../../store/global-selectors';
import { processBulkLoadResponse } from '../../store/products/product-bulk-load-processor';
import { PRODUCT_TYPE_PATH, BASE_PRODUCTS_PATH } from '../../routes/constants';
import { emptyArray } from 'ui-components/default-props/empty';

import style from './shared-classes.module.less';

const ButtonText = (
  <FormattedMessage
    {...defineMessage({
      description: 'orderGridOtherActions.presetGroup',
      defaultMessage: 'Add Preset Group',
      id: '4FJGYl',
    })}
  />
);

const PresetGroupDropdownChild = ({ presetGroup, onClick, disabled }) => {
  const childClicked = useCallback(() => onClick(presetGroup), [presetGroup, onClick]);
  return (
    <DropdownMenuItem className={style['dropdown-menu-item']} disabled={disabled} onClick={childClicked}>
      {presetGroup.name}
    </DropdownMenuItem>
  );
};

PresetGroupDropdownChild.propTypes = {
  presetGroup: ProductPresetGroupPropType.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

const PresetGroupButton = ({ selectedIds = emptyArray }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedProducts = useSelector((state) => Global.selectEntitiesProductsByIds(state, { productIds: selectedIds }));
  const presetGroups = Object.values(useSelector(Global.selectEntitiesProductProfiles));
  const onPresetGroupClicked = useCallback(
    (presetGroup) => {
      addPresetGroupToProducts(selectedProducts, presetGroup.productProfileId).promise.then(() => {
        bulkLoadProducts(selectedIds).then((response) => {
          dispatch(
            entitiesLoad({
              entities: {
                products: createLookupHash(processBulkLoadResponse(response) || [], 'productId'),
              },
            }),
          );
        });
      });
    },
    [selectedProducts, dispatch, selectedIds],
  );

  const redirectToProductTypesPage = useCallback(() => {
    navigate(`${BASE_PRODUCTS_PATH}${PRODUCT_TYPE_PATH}`);
  }, [navigate]);

  return (
    <DropdownMenu closeWhenChildrenClicked dropdownMenuClassName={style['dropdown-menu']} menuPosition='bottom-right'>
      <DropdownMenuToggler className={style['action-button']} disabled={!selectedIds.length} theme='tertiary'>
        {ButtonText}
      </DropdownMenuToggler>
      <DropdownMenuItem onClick={redirectToProductTypesPage}>
        <FormattedMessage
          defaultMessage='Manage Preset Groups'
          description='Button that takes the user to the preset groups page'
          id='jcidK/'
        />
      </DropdownMenuItem>
      {presetGroups.map((presetGroup) => (
        <PresetGroupDropdownChild
          key={presetGroup.productProfileId}
          disabled={!selectedIds.length}
          onClick={onPresetGroupClicked}
          presetGroup={presetGroup}
        />
      ))}
    </DropdownMenu>
  );
};

PresetGroupButton.propTypes = {
  selectedIds: PropTypes.arrayOf(PropTypes.string),
};

export default PresetGroupButton;
